import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import { reservationService } from '../../../services/api';
import { translateRoomType } from '../../../constants/roomTypes';

const RoomDetailsPanel = ({ room, isOpen, onClose, onEdit }) => {
  const [bookingHistory, setBookingHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Carregar histórico de reservas do quarto
  useEffect(() => {
    const loadBookingHistory = async () => {
      if (!room?.id) return;
      
      try {
        setLoadingHistory(true);
        // TODO: Implementar endpoint específico para histórico do quarto
        // const history = await reservationService.getRoomHistory(room.id);
        // setBookingHistory(history);
        
        // Por enquanto, usar dados estáticos
        setBookingHistory([
          {
            id: 1,
            guestName: "Carlos Silva",
            checkIn: "15/01/2025",
            checkOut: "18/01/2025",
            status: "Concluída",
            revenue: 450.00
          },
          {
            id: 2,
            guestName: "Ana Santos",
            checkIn: "20/01/2025",
            checkOut: "22/01/2025",
            status: "Concluída",
            revenue: 300.00
          },
          {
            id: 3,
            guestName: "João Oliveira",
            checkIn: "25/01/2025",
            checkOut: "27/01/2025",
            status: "Ativa",
            revenue: 300.00
          }
        ]);
      } catch (error) {
        console.error('Erro ao carregar histórico do quarto:', error);
        setBookingHistory([]);
      } finally {
        setLoadingHistory(false);
      }
    };

    if (isOpen && room) {
      loadBookingHistory();
    }
  }, [isOpen, room]);

  if (!isOpen || !room) return null;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Disponível':
        return 'bg-success/10 text-success border-success/20';
      case 'Ocupado':
        return 'bg-error/10 text-error border-error/20';
      case 'Manutenção':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'Limpeza':
        return 'bg-secondary/10 text-secondary border-secondary/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-background border-l border-border z-50 overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Bed" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">Quarto {room.number}</h2>
                <p className="text-sm text-muted-foreground">{translateRoomType(room.type)} • Andar {room.floor}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(room)}
                iconName="Edit"
                iconPosition="left"
              >
                Editar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                iconName="X"
                className="p-2"
              />
            </div>
          </div>

          {/* Room Images */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-foreground mb-3">Fotos do Quarto</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                <Image
                  src={room.image}
                  alt={`Quarto ${room.number} - Vista principal`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                <Image
                  src="https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=400"
                  alt={`Quarto ${room.number} - Banheiro`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Room Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground">Informações Básicas</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(room.status)}`}>
                    {room.status}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Capacidade:</span>
                  <div className="flex items-center space-x-1">
                    <Icon name="Users" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-foreground">{room.capacity} pessoas</span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Camas:</span>
                  <div className="flex items-center space-x-1">
                    <Icon name="Bed" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-foreground">{room.beds} camas</span>
                  </div>
                </div>
                {(room.bedsSolteiro > 0 || room.bedsCasal > 0 || room.bedsQueen > 0 || room.bedsKing > 0 || room.bedsBeliche > 0) && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {room.bedsSolteiro > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs bg-muted/50 px-2 py-1 rounded-md">
                        Solteiro: {room.bedsSolteiro}
                      </span>
                    )}
                    {room.bedsCasal > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs bg-muted/50 px-2 py-1 rounded-md">
                        Casal: {room.bedsCasal}
                      </span>
                    )}
                    {room.bedsQueen > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs bg-muted/50 px-2 py-1 rounded-md">
                        Queen: {room.bedsQueen}
                      </span>
                    )}
                    {room.bedsKing > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs bg-muted/50 px-2 py-1 rounded-md">
                        King: {room.bedsKing}
                      </span>
                    )}
                    {room.bedsBeliche > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs bg-muted/50 px-2 py-1 rounded-md">
                        Beliche: {room.bedsBeliche}
                      </span>
                    )}
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Área:</span>
                  <span className="text-sm text-foreground">{room.area || '25'} m²</span>
                </div>
              </div>

              {/* Características do Quarto */}
              <div className="pt-4 border-t border-border">
                <h4 className="text-sm font-medium text-foreground mb-3">Características</h4>
                <div className="flex flex-wrap gap-3 items-center">
                  <Icon name="Users" size={18} color="var(--color-primary)" title={`Capacidade: ${room.capacity} pessoas`} />
                  {room.hasAC && <Icon name="Wind" size={18} color="var(--color-primary)" title="Ar Condicionado" />}
                  {room.hasMinibar && <Icon name="Coffee" size={18} color="var(--color-secondary)" title="Frigobar" />}
                  {room.hasBathroom && <Icon name="Bath" size={18} color="var(--color-accent)" title="Banheiro Privativo" />}
                </div>
              </div>
            </div>

            {/* Pricing */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-foreground">Preços</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Ocupação Solteiro:</span>
                  <span className="text-sm font-medium text-foreground">{formatPrice(room.singlePrice)}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Ocupação Casal:</span>
                  <span className="text-sm font-medium text-foreground">{formatPrice(room.couplePrice)}</span>
                </div>
                
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <span className="text-sm text-muted-foreground">Receita Mensal:</span>
                  <span className="text-sm font-semibold text-success">{formatPrice(room.monthlyRevenue || 2850)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Comodidades Extras */}
          {room.description && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-foreground mb-3">Descrição</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{room.description}</p>
            </div>
          )}

          {/* Booking History */}
          <div>
            <h3 className="text-lg font-medium text-foreground mb-3">Histórico de Reservas</h3>
            <div className="space-y-3">
              {loadingHistory ? (
                <p className="text-center text-muted-foreground">Carregando histórico...</p>
              ) : bookingHistory.length === 0 ? (
                <p className="text-center text-muted-foreground">Nenhum histórico de reserva encontrado para este quarto.</p>
              ) : (
                bookingHistory.map((booking) => (
                  <div key={booking.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div>
                      <div className="text-sm font-medium text-foreground">{booking.guestName}</div>
                      <div className="text-xs text-muted-foreground">
                        {booking.checkIn} - {booking.checkOut}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-foreground">{formatPrice(booking.revenue)}</div>
                      <div className={`text-xs ${booking.status === 'Ativa' ? 'text-success' : 'text-muted-foreground'}`}>
                        {booking.status}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RoomDetailsPanel;